import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import shallow from 'zustand/shallow';
import Modal from '@root/components/modal';
import Toast from '@root/components/toast';
import useCreateOrder from '../newSubscription/useCreateOrder';
import { useDefaultPackageState, useUnBindDevicesState } from '@root/state';
import { IPackagePrice } from '@root/types/package';
import {
  EPayMode,
  PROMOTIONS_ACTIVITY_ID,
  UNLIMITED_PACKAGE_NUMBER,
} from '@root/constant';
import usePayMethod, { IPayMethodProps } from '../usePayMethod';
import { loggerOperatorOnTrack } from '@root/logger/loggerOperator';
import { getQueryString } from '@root/shared';
import { useCurrentHouseId } from '../useHouseList';

const SENSOR_PAY_MODE: any = {
  [EPayMode.PayPal]: 'PayPal',
  [EPayMode.CreditCard]: 'Credit Card',
};

interface IProps {
  isPromotions?: boolean;
}

const useSelectDevices = (
  props?: IProps,
): {
  payMethodState: IPayMethodProps;
  selectedPrice: IPackagePrice;
  selected: string[];
  maxDevNum?: number;
  paying?: boolean;
  isUnlimited?: boolean;
  onSelected: (vs: any[]) => void;
  onClickPay: () => void;
} => {
  const { isPromotions } = props || {};
  const navigate = useNavigate();
  const payMethodState = usePayMethod();
  const { paying, onCreateOrder } = useCreateOrder({ isPromotions });
  const { t } = useTranslation();
  const { selectedPrice, curPackage } = useDefaultPackageState(
    (state) => ({
      curPackage: state.packageData,
      selectedPrice: state.selectedPrice,
    }),
    shallow,
  );
  const currentHouseId = useCurrentHouseId();
  const unBindDevices = useUnBindDevicesState((state) => state.list);
  const [selected, setSelected] = useState<string[]>([]);
  const { payMethod } = payMethodState;

  const sensorOriginal = getQueryString('sensor') || '';

  const sensorTrack = useCallback(
    (params = {}) => {
      // 年订阅活动页
      if (isPromotions) {
        loggerOperatorOnTrack('CloudServiceClickPurchase', {
          promotions_id: PROMOTIONS_ACTIVITY_ID,
          original: 'promotions',
          ...params,
        });
        return;
      }

      // 神策数据上报 云回放或者直播过来的才需要上报
      if (sensorOriginal) {
        loggerOperatorOnTrack('CloudServiceClickPurchase', {
          original: sensorOriginal,
          ...params,
        });
      }
    },
    [isPromotions, sensorOriginal],
  );

  const isUnlimited = selectedPrice.buyNum === UNLIMITED_PACKAGE_NUMBER;

  const maxDevNum = curPackage.maxDevNum || 0;

  const onSelected = useCallback((vs: any[]) => {
    setSelected(vs);
  }, []);

  const onClickPay = useCallback(() => {
    if (!curPackage.goodsId) {
      navigate(-1);
      return;
    }

    if (!isUnlimited && selected.length > maxDevNum) {
      Toast.info(t('service_max', { maxDevNum }));
      return;
    }

    const sensorParams = {
      pay_mode: SENSOR_PAY_MODE[payMethod] || '',
      select_ids: selected.toString(),
      price: `${selectedPrice.price}`,
    };

    if (!selected.length) {
      Modal.confirm({
        footerColumn: true,
        content: t('plan_detail_unselect_tips'),
        confirm: t('unselected_continue'),
        cancel: t('cancel'),
        onConfirm: () => {
          onCreateOrder({
            packageInfo: curPackage,
            priceInfo: selectedPrice,
            payMode: payMethod,
          });
          sensorTrack(sensorParams);
        },
      });
      return;
    }

    onCreateOrder({
      packageInfo: curPackage,
      priceInfo: selectedPrice,
      deviceIds: selected,
      payMode: payMethod,
    });
    sensorTrack(sensorParams);
  }, [
    curPackage,
    isUnlimited,
    selected,
    maxDevNum,
    payMethod,
    selectedPrice,
    onCreateOrder,
    sensorTrack,
    navigate,
    t,
  ]);

  useEffect(() => {
    setSelected([]);
  }, [currentHouseId]);

  useEffect(() => {
    if (!curPackage.goodsId) {
      navigate(-1);
    }
  }, [curPackage, navigate]);

  useEffect(() => {
    if (isUnlimited) {
      setSelected((v) => (v.length ? v : unBindDevices.map((i) => i.deviceId)));
    }
  }, [isUnlimited, unBindDevices]);

  useEffect(() => {
    return () => {
      Toast.destroy();
      Modal.close();
    };
  }, []);

  return {
    payMethodState,
    selectedPrice,
    isUnlimited,
    selected,
    maxDevNum,
    paying,
    onSelected,
    onClickPay,
  };
};

export default useSelectDevices;
